import { useState } from "react";
import { Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CatalogList from "@/pages/admin/CatalogList";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { SimpleSchemeForm, SchemeEditForm, looksLikeSimpleScheme } from "@/pages/admin/CatalogForms";

// The "Active" switch on the SKUs list. Disabling here already hides the
// SKU from Primary/Secondary order pickers (skus.is_active is a hard filter
// there) — but distributor_stock.is_active is a SEPARATE flag, so without
// this cascade, Inventory (and Secondary Order's own distributor_stock
// check) would keep showing the SKU "active" per distributor. Re-enabling
// deliberately does NOT cascade back on — an admin may have turned a
// distributor's stock off for its own reason, unrelated to the SKU-level
// flag, and flipping this back on shouldn't silently undo that.
function SkuActiveToggle({ row, reload }) {
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const apply = async (is_active, cascade) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/skus/${row.id}`, {
        is_active, cascade_deactivate_stock: cascade,
      });
      const n = data.cascaded_distributor_stock_count || 0;
      toast.success(
        is_active
          ? `${row.sku_code} activated`
          : `${row.sku_code} deactivated` + (n > 0 ? ` — also turned off for ${n} distributor${n === 1 ? "" : "s"}` : "")
      );
      reload?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Switch
          checked={!!row.is_active}
          disabled={saving}
          onCheckedChange={(checked) => (checked ? apply(true, false) : setConfirmOpen(true))}
          data-testid={`catalog-skus-active-toggle-${row.sku_code}`}
        />
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="catalog-skus-deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate {row.sku_code}?</DialogTitle>
            <DialogDescription>
              This removes it from Primary and Secondary order pickers everywhere,
              and also turns off its stock status for every distributor currently
              carrying it (so it disappears from Inventory and Secondary Order too,
              not just new orders). This does not delete any stock data — it can be
              re-enabled per distributor later from the Inventory screen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" disabled={saving}
                    onClick={() => apply(false, true)}
                    data-testid="catalog-skus-deactivate-confirm">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Same grouping key SimpleSchemeForm's edit-open sibling fetch uses (an
// explicit scheme_group_id when the record has one, else an exact
// name+channel+"simple wizard shape" fallback for legacy groups) — so the
// list collapses to one row per group exactly matching what Edit will load.
function schemeGroupKey(row) {
  if (row.scheme_group_id) return `g:${row.scheme_group_id}`;
  if (row.ui_source === "simple" || looksLikeSimpleScheme(row)) {
    return `n:${row.name}||${row.order_channel || "SECONDARY"}`;
  }
  return null;
}

export function ProductsList() {
  return (
    <CatalogList
      title="Products" subtitle="Category → Variant → Pack Size"
      masterKey="products" apiPath="/products" entityType="products"
      searchPlaceholder="Search by code or name…"
      columns={[
        { key: "code", label: "Code" },
        { key: "name", label: "Name" },
        { key: "brand", label: "Brand" },
        { key: "hsn", label: "HSN" },
        // Drop 9-Hotfix — surface joined lookup codes so imported values
        // are visible in the admin table.
        { key: "category_code", label: "Category",
          render: (r) => r.category_code || "—" },
        { key: "variant_code", label: "Variant",
          render: (r) => r.variant_code || "—" },
        { key: "pack_size_code", label: "Pack size",
          render: (r) => r.pack_size_code || "—" },
        { key: "is_active", label: "Active",
          render: (r) => r.is_active ? "Yes" : "No" },
      ]}
    />
  );
}

export function SkusList() {
  return (
    <CatalogList
      title="SKUs" subtitle="Stock-Keeping Units"
      masterKey="skus" apiPath="/skus" entityType="skus"
      searchPlaceholder="Search by sku_code or barcode…"
      columns={[
        { key: "sku_code", label: "SKU code" },
        // Drop 9-Hotfix — surface the parent product's name (was hidden
        // under raw product_id).
        { key: "product_name", label: "Product",
          render: (r) => r.product_name || r.product_code || "—" },
        { key: "mrp", label: "MRP", render: (r) => `₹ ${(r.mrp ?? 0).toLocaleString()}` },
        { key: "base_price", label: "Base price",
          render: (r) => `₹ ${(r.base_price ?? 0).toLocaleString()}` },
        { key: "gst_rate", label: "GST %" },
        { key: "uom", label: "UOM" },
        { key: "is_active", label: "Active",
          render: (r, _enrichment, reload) => <SkuActiveToggle row={r} reload={reload} /> },
      ]}
    />
  );
}

export function PricingList() {
  return (
    <CatalogList
      title="Pricing" subtitle="Zone- and distributor-level slabs"
      masterKey="pricing" apiPath="/pricing" entityType="pricing"
      searchPlaceholder="Search by SKU code…"
      showEffectiveTester
      extraToolbar={
        <BulkActionsBar
          masterKey="distributor_price"
          label="Distributor Price (flat)"
          canImport canExport
          onImportDone={() => window.location.reload()}
        />
      }
      columns={[
        // Enriched by backend: sku_code, scope_code, scope_name (see routes_catalog.list_pricing).
        // Zero raw UUID exposure (UAT RC1b).
        { key: "sku_code", label: "SKU",
          render: (r) => r.sku_code || <span className="text-neutral-400">—</span> },
        { key: "scope_type", label: "Scope" },
        { key: "scope_name", label: "Applied to",
          render: (r) => r.scope_code
            ? <span><span className="font-mono">{r.scope_code}</span>{r.scope_name ? ` — ${r.scope_name}` : ""}</span>
            : <span className="text-neutral-400">—</span> },
        { key: "slabs", label: "Slabs",
          render: (r) => (r.slabs || []).map(s =>
            `${s.min_qty}-${s.max_qty ?? "∞"}: ₹${s.price}`).join(" | ") },
        { key: "valid_from", label: "From" },
        { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" },
      ]}
    />
  );
}

export function SchemesList() {
  const [showSimple, setShowSimple] = useState(false);
  const [reloadSeq, setReloadSeq] = useState(0);
  return (
    <>
      <CatalogList
        key={reloadSeq}
        title="Schemes" subtitle="Promotional rules and free-goods"
        masterKey="schemes" apiPath="/schemes" entityType="schemes"
        searchPlaceholder="Search by code or name…"
        editForm={SchemeEditForm}
        groupBy={schemeGroupKey}
        pageSize={500}
        extraToolbar={
          <Button variant="outline" onClick={() => setShowSimple(true)}
                  className="h-10" data-testid="catalog-schemes-simple-new">
            <Wand2 className="h-4 w-4 mr-2" /> Simple scheme
          </Button>
        }
        columns={[
          { key: "code", label: "Code",
            render: (r) => r._groupSize > 1
              ? <span>{r.code} <span className="text-neutral-400">+{r._groupSize - 1} more</span></span>
              : r.code },
          { key: "name", label: "Name" },
          { key: "skus", label: "SKUs", render: (r) => r._groupSize || 1 },
          { key: "scheme_type", label: "Type" },
          { key: "order_channel", label: "Channel", render: (r) => r.order_channel || "SECONDARY" },
          { key: "priority", label: "Priority" },
          { key: "valid_from", label: "From" },
          { key: "valid_to", label: "To" },
          { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" },
        ]}
      />
      <SimpleSchemeForm open={showSimple} onOpenChange={setShowSimple}
                        onSaved={() => setReloadSeq(s => s + 1)} />
    </>
  );
}
